import { classNames, useState } from 'libs/react';
import { useMemo } from 'react';
import { React } from 'libs/react';
import { Money } from 'components/Money';
import { Image } from 'components/Image';
import { Icon } from 'components/Icon';
import { useModal } from 'components/modal/ModalStack';
import { SaleItemViewer } from 'components/saleItem/SaleItemViewer';
import i18n from 'components/translation/i18n';
import { getDealItemTotalWithQuantity } from 'helpers/Pricing';
import { getDealItemBundledExtensionParts } from 'helpers/quoteBundled';
import { getDealItemName } from 'helpers/Deal';
import { formatMoney } from 'helpers/format';
import { isRichTextEmpty } from 'helpers/richText';
import { thumbnailSource } from 'helpers/mediaSources';
import { saleItemHasViewerContent } from './saleItemViewerContent';
import { StoreFeeInfos } from './components/StoreFeeInfos';
import { useStoreOfferedUnits } from './useStoreOfferedUnits';
import './StoreQuoteSummaryPanel.scss';
function getOptionName(config) {
    var _a;
    // Public SaleItem.title is already resolved for the deal language.
    return ((_a = config.item) === null || _a === void 0 ? void 0 : _a.title) || '';
}
function resolveCatalogSaleItem(item, saleItems) {
    var _a, _b;
    if (!(item === null || item === void 0 ? void 0 : item.id)) {
        return item !== null && item !== void 0 ? item : undefined;
    }
    const fromCatalog = saleItems.find((entry) => entry.id === item.id);
    if (fromCatalog) {
        return fromCatalog;
    }
    for (const parent of saleItems) {
        const nested = (_b = (_a = parent.options) === null || _a === void 0 ? void 0 : _a.find((option) => { var _a; return ((_a = option.item) === null || _a === void 0 ? void 0 : _a.id) === item.id; })) === null || _b === void 0 ? void 0 : _b.item;
        if (nested) {
            return nested;
        }
    }
    return item;
}
function getItemImageUrl(item) {
    var _a, _b;
    const image = (_b = (_a = item.item) === null || _a === void 0 ? void 0 : _a.image) === null || _b === void 0 ? void 0 : _b.image;
    return image ? thumbnailSource(image) : undefined;
}
/**
 * @component StoreQuoteSummaryPanel
 * @since 1.0.0
 */
export function StoreQuoteSummaryPanel({ deal, configurationFields = [], saleItems = [], defaultCollapsed = true, editable = false, isUpdating = false, onRemoveItem, onRemoveOption, promoCodeField }) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const language = i18n.language.substring(0, 2);
    const modal = useModal();
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const isBundled = (deal === null || deal === void 0 ? void 0 : deal.priceDisplay) === 'BUNDLED';
    const items = useMemo(() => { var _a, _b; return (_b = (_a = deal === null || deal === void 0 ? void 0 : deal.items) === null || _a === void 0 ? void 0 : _a.filter((item) => !item.proposed)) !== null && _b !== void 0 ? _b : []; }, [deal]);
    const lineBonuses = useStoreOfferedUnits(deal);
    const otherFees = (_a = deal === null || deal === void 0 ? void 0 : deal.otherFees) !== null && _a !== void 0 ? _a : [];
    // `subtotal` is net of the discount: show the lines before it, then the
    // discount on its own row.
    const discountAmount = (_c = (_b = deal === null || deal === void 0 ? void 0 : deal.promoCode) === null || _b === void 0 ? void 0 : _b.discountAmount) !== null && _c !== void 0 ? _c : 0;
    const grossSubtotal = ((_d = deal === null || deal === void 0 ? void 0 : deal.subtotal) !== null && _d !== void 0 ? _d : 0) + discountAmount;
    const itemCount = items.reduce((acc, item) => acc + item.quantity, 0);
    /**
     * @function onItemInfoClick
     * @since 1.0.0
     * @hidden
     */
    function onItemInfoClick(dealItem) {
        var _a;
        const item = resolveCatalogSaleItem(dealItem.item, saleItems);
        if (!item) {
            return;
        }
        modal.present({
            title: getDealItemName(dealItem, language, configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], []),
            picto: '',
            className: 'deal-sale-item-selector-item-modal',
            modal: (React.createElement(SaleItemViewer, { item: item, lang: language, variant: (_a = dealItem.variant) !== null && _a !== void 0 ? _a : undefined, isStore: true }))
        });
    }
    /**
     * @function onOptionInfoClick
     * @since 1.0.0
     * @hidden
     */
    function onOptionInfoClick(option) {
        const item = resolveCatalogSaleItem(option.item, saleItems);
        if (!item) {
            return;
        }
        modal.present({
            title: getOptionName(option) || item.sku,
            picto: '',
            className: 'deal-sale-item-selector-item-modal',
            modal: React.createElement(SaleItemViewer, { item: item, lang: language, isStore: true })
        });
    }
    return (React.createElement("aside", { className: "store-summary", "aria-label": t('Order summary') },
        React.createElement("button", { type: "button", className: "store-summary__toggle", onClick: () => setCollapsed((c) => !c), "aria-expanded": !collapsed },
            React.createElement("span", { className: "store-summary__toggle-main" },
                React.createElement("span", { className: "store-summary__toggle-label" }, t('Order summary')),
                React.createElement("span", { className: "store-summary__toggle-meta" },
                    itemCount,
                    " ",
                    itemCount === 1 ? t('item') : t('items'))),
            (deal === null || deal === void 0 ? void 0 : deal.total) != null && (React.createElement("span", { className: "store-summary__toggle-total" },
                React.createElement(Money, { value: deal.total }))),
            React.createElement(Icon, { className: classNames('store-summary__toggle-icon', !collapsed && 'store-summary__toggle-icon--open'), value: "chevron-down" })),
        React.createElement("div", { className: classNames('store-summary__body', collapsed && 'store-summary__body--collapsed') },
            React.createElement("header", { className: "store-summary__header" },
                React.createElement("h3", { className: "store-summary__title" }, t('Order summary')),
                React.createElement("span", { className: "store-summary__count" },
                    itemCount,
                    " ",
                    itemCount === 1 ? t('item') : t('items'))),
            items.length > 0 && (React.createElement("ul", { className: "store-summary__items" }, items.map((item) => {
                var _a, _b, _c;
                const bundledParts = isBundled
                    ? getDealItemBundledExtensionParts(item, deal)
                    : null;
                const itemTotal = isBundled && bundledParts
                    ? bundledParts.bundledExtension
                    : getDealItemTotalWithQuantity(item, true, deal);
                const selectedOptions = (_b = (_a = item.options) === null || _a === void 0 ? void 0 : _a.filter((opt) => !opt.proposed)) !== null && _b !== void 0 ? _b : [];
                const offered = lineBonuses.offered.get(item.id);
                const monetaryBonus = lineBonuses.monetary.get(item.id);
                const imageUrl = getItemImageUrl(item);
                const catalogItem = resolveCatalogSaleItem(item.item, saleItems);
                const showItemInfo = saleItemHasViewerContent(catalogItem, language);
                return (React.createElement("li", { className: "store-summary__item", key: item.id },
                    React.createElement("div", { className: "store-summary__item-media" },
                        imageUrl ? (React.createElement(Image, { className: "store-summary__item-image", source: imageUrl, cover: true })) : (React.createElement("div", { className: "store-summary__item-image store-summary__item-image--placeholder", "aria-hidden": true })),
                        React.createElement("span", { className: "store-summary__item-qty-badge" }, item.quantity)),
                    React.createElement("div", { className: "store-summary__item-details" },
                        React.createElement("div", { className: "store-summary__item-row" },
                            React.createElement("div", { className: "store-summary__item-name-row" },
                                React.createElement("span", { className: "store-summary__item-name" }, getDealItemName(item, language, configurationFields !== null && configurationFields !== void 0 ? configurationFields : [], [])),
                                showItemInfo && (React.createElement("button", { type: "button", className: "store-summary__info", "aria-label": t('View details'), onClick: () => onItemInfoClick(item) },
                                    React.createElement(Icon, { value: "circle-info" })))),
                            React.createElement("div", { className: "store-summary__item-row-end" },
                                React.createElement(Money, { className: "store-summary__item-price", value: itemTotal }),
                                editable && items.length > 1 && onRemoveItem && (React.createElement("button", { type: "button", className: "store-summary__remove", "aria-label": t('Remove'), disabled: isUpdating, onClick: () => onRemoveItem(item) },
                                    React.createElement(Icon, { value: "trash-can" }))))),
                        (offered ||
                            monetaryBonus ||
                            selectedOptions.length > 0) && (React.createElement("ul", { className: "store-summary__item-options" },
                            offered && (React.createElement("li", { className: "store-summary__option-row store-summary__option-row--offered" },
                                React.createElement("div", { className: "store-summary__option-name-row" },
                                    React.createElement("span", { className: "store-summary__option-name" }, (_c = offered.label) !== null && _c !== void 0 ? _c : t('Bonus'))),
                                React.createElement("div", { className: "store-summary__option-row-end" },
                                    React.createElement("span", { className: "store-summary__option-badge" },
                                        React.createElement(Icon, { value: "gift" }),
                                        offered.quantity === 1
                                            ? t('1 offered')
                                            : `${offered.quantity} ${t('offered units')}`)))),
                            monetaryBonus && (React.createElement("li", { className: "store-summary__option-row store-summary__option-row--offered" },
                                React.createElement("div", { className: "store-summary__option-name-row" },
                                    React.createElement("span", { className: "store-summary__option-name" }, monetaryBonus.labels.join(' · '))),
                                React.createElement("div", { className: "store-summary__option-row-end" },
                                    React.createElement("span", { className: "store-summary__option-badge" },
                                        React.createElement(Icon, { value: "gift" }),
                                        monetaryBonus.bonusVoucherAmount
                                            ? t('Voucher of {{amount}} + bonus voucher of {{bonus}}')
                                                .replace('{{amount}}', formatMoney(monetaryBonus.unitAmount))
                                                .replace('{{bonus}}', formatMoney(monetaryBonus.bonusVoucherAmount))
                                            : t('Voucher of {{amount}}').replace('{{amount}}', formatMoney(monetaryBonus.unitAmount)))))),
                            selectedOptions.map((opt) => {
                                const optionItem = resolveCatalogSaleItem(opt.item, saleItems);
                                const showOptionInfo = saleItemHasViewerContent(optionItem, language, { textOnly: true });
                                return (React.createElement("li", { className: "store-summary__option-row", key: opt.id },
                                    React.createElement("div", { className: "store-summary__option-name-row" },
                                        React.createElement("span", { className: "store-summary__option-name" },
                                            opt.quantity > 1 && `${opt.quantity}× `,
                                            getOptionName(opt)),
                                        showOptionInfo && (React.createElement("button", { type: "button", className: "store-summary__info store-summary__info--option", "aria-label": t('View details'), onClick: () => onOptionInfoClick(opt) },
                                            React.createElement(Icon, { value: "circle-info" })))),
                                    React.createElement("div", { className: "store-summary__option-row-end" }, opt.included ? (React.createElement("span", { className: "store-summary__option-badge" },
                                        React.createElement(Icon, { value: "check" }),
                                        t('Included'))) : (React.createElement(React.Fragment, null,
                                        React.createElement(Money, { className: "store-summary__option-price", value: opt.price * opt.quantity }),
                                        editable && onRemoveOption && (React.createElement("button", { type: "button", className: "store-summary__remove store-summary__remove--option", "aria-label": t('Remove'), disabled: isUpdating, onClick: () => onRemoveOption(item, opt) },
                                            React.createElement(Icon, { value: "trash-can" }))))))));
                            }))))));
            }))),
            otherFees.length > 0 && (React.createElement("div", { className: "store-summary__fees" },
                React.createElement("div", { className: "store-summary__fees-title" }, t('Other fees')),
                otherFees.map((fee, i) => {
                    var _a, _b;
                    return (React.createElement("div", { className: "store-summary__fee-row", key: i },
                        React.createElement("span", { className: "store-summary__fee-name" },
                            fee.description || ((_a = fee.fee) === null || _a === void 0 ? void 0 : _a.title) || t('Fee'),
                            ((_b = fee.fee) === null || _b === void 0 ? void 0 : _b.infos) && !isRichTextEmpty(fee.fee.infos) && (React.createElement(StoreFeeInfos, { infos: fee.fee.infos, label: fee.description || fee.fee.title }))),
                        fee.price > 0 && (React.createElement(Money, { className: "store-summary__fee-price", value: fee.price })),
                        fee.price === 0 && (React.createElement("span", { className: "store-summary__fee-tbd" }, t('TBD')))));
                }))),
            promoCodeField,
            React.createElement("div", { className: "store-summary__totals" },
                (grossSubtotal !== ((_e = deal === null || deal === void 0 ? void 0 : deal.total) !== null && _e !== void 0 ? _e : 0) || discountAmount > 0) && (React.createElement("div", { className: "store-summary__total-row" },
                    React.createElement("span", null, t('Subtotal')),
                    React.createElement(Money, { value: grossSubtotal }))),
                discountAmount > 0 && (deal === null || deal === void 0 ? void 0 : deal.promoCode) && (React.createElement("div", { className: "store-summary__total-row store-summary__total-row--discount" },
                    React.createElement("span", null,
                        t('Promo code'),
                        " ",
                        deal.promoCode.code),
                    React.createElement(Money, { value: -discountAmount }))),
                (deal === null || deal === void 0 ? void 0 : deal.taxes) && deal.taxes.length > 0
                    ? deal.taxes.map((dealTax) => (React.createElement("div", { className: "store-summary__total-row", key: dealTax.id },
                        React.createElement("span", null,
                            dealTax.tax.title,
                            " (",
                            dealTax.rate,
                            "%)"),
                        React.createElement(Money, { value: dealTax.value }))))
                    : ((_f = deal === null || deal === void 0 ? void 0 : deal.taxAmount) !== null && _f !== void 0 ? _f : 0) > 0 && (React.createElement("div", { className: "store-summary__total-row" },
                        React.createElement("span", null, t('Taxes')),
                        React.createElement(Money, { value: (_g = deal === null || deal === void 0 ? void 0 : deal.taxAmount) !== null && _g !== void 0 ? _g : 0 }))),
                React.createElement("div", { className: "store-summary__total-row store-summary__total-row--grand" },
                    React.createElement("span", null, t('Total')),
                    React.createElement(Money, { value: (_h = deal === null || deal === void 0 ? void 0 : deal.total) !== null && _h !== void 0 ? _h : 0 }))))));
}
